import React from 'react';
import { groupExposedPorts } from '../utils/exposedPorts';

const CHIP =
  'inline-flex items-center rounded-md border px-1.5 py-0.5 font-mono text-[10px] font-semibold';

/**
 * Categorized open-port chips (Web / Mail / Admin) for infra cards & drawers.
 *
 * @param {{ ports?: unknown, className?: string, emptyLabel?: string }} props
 */
export default function ExposedPortsGroups({
  ports,
  className = '',
  emptyLabel = 'None observed',
}) {
  const grouped = groupExposedPorts(ports);
  const hasAny =
    grouped.web.length ||
    grouped.mail.length ||
    grouped.admin.length ||
    grouped.other.length;

  if (!hasAny) {
    return (
      <p className={`font-mono text-xs text-slate-500 ${className}`.trim()} data-testid="exposed-ports-empty">
        {emptyLabel}
      </p>
    );
  }

  return (
    <div className={`space-y-2 ${className}`.trim()} data-testid="exposed-ports-groups">
      {grouped.web.length > 0 ? (
        <div data-testid="ports-group-web">
          <p className="mb-1 text-[9px] font-black uppercase tracking-widest text-slate-500">
            🌐 Web
          </p>
          <div className="flex flex-wrap gap-1">
            {grouped.web.map((p) => (
              <span
                key={`web-${p}`}
                className={`${CHIP} border-cyan-500/35 bg-cyan-950/30 text-cyan-200`}
              >
                {p}
              </span>
            ))}
          </div>
        </div>
      ) : null}

      {grouped.mail.length > 0 ? (
        <div data-testid="ports-group-mail">
          <p className="mb-1 text-[9px] font-black uppercase tracking-widest text-slate-500">
            ✉️ Mail
          </p>
          <div className="flex flex-wrap gap-1">
            {grouped.mail.map((p) => (
              <span
                key={`mail-${p}`}
                className={`${CHIP} border-sky-500/35 bg-sky-950/30 text-sky-200`}
              >
                {p}
              </span>
            ))}
          </div>
        </div>
      ) : null}

      {grouped.admin.length > 0 ? (
        <div data-testid="ports-group-admin">
          <p className="mb-1 text-[9px] font-black uppercase tracking-widest text-slate-500">
            ⚠️ Admin / Core
          </p>
          <div className="flex flex-wrap gap-1">
            {grouped.admin.map(({ port, label }) => (
              <span
                key={`admin-${port}`}
                className={`${CHIP} border-rose-500/50 bg-rose-950/20 text-rose-300`}
                title={label}
              >
                {port} ({label})
              </span>
            ))}
          </div>
        </div>
      ) : null}

      {grouped.other.length > 0 ? (
        <div data-testid="ports-group-other">
          <p className="mb-1 text-[9px] font-black uppercase tracking-widest text-slate-500">
            Other
          </p>
          <div className="flex flex-wrap gap-1">
            {grouped.other.map((p) => (
              <span
                key={`other-${p}`}
                className={`${CHIP} border-slate-600/50 bg-slate-900/60 text-slate-300`}
              >
                {p}
              </span>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
